import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { jbApi } from '../job-board/api';
import { useHeroImage } from '../hooks/usePageHero';
import { Heart, Landmark, Briefcase, GraduationCap, Headphones, Clock, Grid2X2, Cpu, Users, MailOpen, Mail, BellRing, Bell, Timer, Newspaper, Crosshair, ChevronRight } from 'lucide-react';
import { MostEngagedWidget } from '../components/MostEngagedWidget';
import { LastCallWidget } from '../components/LastCallWidget';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ─── Newsroom image URL (ImageKit-swappable — update this constant to change the image) ──
const NEWSROOM_IMAGE_URL = 'https://ik.imagekit.io/enrollforge/bg/HPnewsroom1.webp';

// ─── First Round waveform bar heights ────────────────────────────────────────
const WAVE_BARS = [4,9,16,24,19,29,36,43,39,46,51,45,37,43,49,41,36,29,23,31,39,45,41,33,26,19,13,9,6,11,17,25,31,21,15,9,5,8,15,21,27,33,29,23,17,11,7,5];
const FR_WAVE_BARS = [18,28,42,32,48,36,22,40,30,44,20,38,48,26,36,44,22,34,46,30,18,40,34,48,24,38,28,44,20,36,46,28,40,32,48,22,36,42,30,26];

// ─── Sector definitions ───────────────────────────────────────────────────────
const SECTORS_DEF = [
  {
    key: 'healthcare',
    name: 'Healthcare',
    desc: 'Hospitals, care access, workforce, public health, and health equity.',
    accent: 'var(--op-teal)',
    accentRaw: '#008C86',
    iconBg: 'rgba(0,140,134,.12)',
    link: '/coverage/healthcare',
    cta: 'View Healthcare',
    Icon: Heart,
  },
  {
    key: 'government',
    name: 'Government',
    desc: 'Federal, state, and local policy, public-sector jobs, and grants.',
    accent: 'var(--op-gold)',
    accentRaw: '#D9A245',
    iconBg: 'rgba(217,162,69,.14)',
    link: '/coverage/government',
    cta: 'View Government',
    Icon: Landmark,
  },
  {
    key: 'business-finance',
    name: 'Business & Finance',
    desc: 'Employers, entrepreneurship, workforce, money, and leadership.',
    accent: 'var(--op-navy)',
    accentRaw: '#081D30',
    iconBg: 'rgba(8,29,48,.08)',
    link: '/coverage/business-finance',
    cta: 'View Business',
    Icon: Briefcase,
  },
  {
    key: 'education',
    name: 'Education',
    desc: 'Colleges, training, scholarships, and student opportunity.',
    accent: '#7B6E9C',
    accentRaw: '#7B6E9C',
    iconBg: 'rgba(123,110,156,.12)',
    link: '/coverage/education',
    cta: 'View Education',
    Icon: GraduationCap,
  },
  {
    key: 'technology-ai',
    name: 'Technology & AI',
    desc: 'AI, civic tech, digital transformation, and the future of work.',
    accent: '#1B6B72',
    accentRaw: '#1B6B72',
    iconBg: 'rgba(27,107,114,.10)',
    link: '/coverage/technology-ai',
    cta: 'View Technology',
    Icon: Cpu,
  },
  {
    key: 'workforce-labor',
    name: 'Workforce & Labor',
    desc: 'Jobs, wages, unions, economic mobility, and workforce development.',
    accent: '#B45309',
    accentRaw: '#B45309',
    iconBg: 'rgba(180,83,9,.10)',
    link: '/coverage/workforce-labor',
    cta: 'View Workforce',
    Icon: Users,
  },
];

// ─── Main export ──────────────────────────────────────────────────────────────
export const HomePage = () => {
  const [homepageData, setHomepageData] = useState(null);
  const [contentItems, setContentItems] = useState([]);
  const [featuredJobs, setFeaturedJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [hpRes, contentRes, jobsRes] = await Promise.all([
          axios.get(`${API}/homepage`),
          axios.get(`${API}/content`, {
            params: { status: 'published', distribution_target: 'website', sort: 'published_at_desc', limit: 30 },
          }).catch(() => ({ data: [] })),
          jbApi.homepageFeatured().catch(() => ({ jobs: [] })),
        ]);
        setHomepageData(hpRes.data);
        setContentItems(contentRes.data || []);
        setFeaturedJobs((jobsRes.jobs || []).slice(0, 4));
      } catch (err) {
        console.error('Homepage load error:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div
        className="op-main min-h-screen flex items-center justify-center"
        data-testid="loading-spinner"
      >
        <div className="text-center">
          <div
            className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4"
            style={{ borderColor: 'var(--op-teal)' }}
          />
          <p style={{ color: 'var(--op-muted)', fontSize: '0.9375rem' }}>Loading...</p>
        </div>
      </div>
    );
  }

  const latestMagazine = homepageData?.latest_magazine || null;
  const signalArticle =
    homepageData?.featured_article || homepageData?.latest_articles?.[0] || null;

  const now = new Date();
  const oneWeekAgo = new Date(now - 7 * 24 * 60 * 60 * 1000);
  const freshThisWeek = contentItems.filter(
    (item) => item.published_at && new Date(item.published_at) > oneWeekAgo
  );

  return (
    <div className="op-main" data-testid="homepage">
      {/* ── 1. Hero ── */}
      <HeroSection />

      {/* ── 2. Fresh This Week card ── */}
      <div className="op-fresh-card-wrap">
        <div className="container-custom">
          <FreshThisWeekCard
            freshCount={freshThisWeek.length}
          />
        </div>
      </div>

      {/* ── 3. Gateway: First Round · Newsroom · Magazine ── */}
      <GatewaySection latestMagazine={latestMagazine} newsroomImageUrl={homepageData?.newsroom_image_url} />

      {/* ── 4. Featured Opportunities — disabled until live jobs are connected ── */}
      {false && featuredJobs.length > 0 && (
        <div className="container-custom" id="opportunities">
          <FeaturedOpportunitiesModule jobs={featuredJobs} />
        </div>
      )}

      {/* ── 5. Trending Sectors ── */}
      <div className="container-custom">
        <TrendingSectorsSection contentItems={contentItems} />
      </div>

      {/* ── 6. Most Engaged + Last Call Sector Briefs ── */}
      <div className="container-custom op-sb-outer">
        <div className="op-sb-dual-row">
          <MostEngagedWidget />
          <LastCallWidget />
        </div>
      </div>

    </div>
  );
};

// ─── Hero ─────────────────────────────────────────────────────────────────────
const HeroSection = () => {
  const heroImg = useHeroImage('home');
  return (
    <section
      className="op-hero"
      data-testid="hero-section"
      aria-labelledby="op-hero-title"
      style={heroImg ? { background: `url(${heroImg}) center/cover no-repeat` } : undefined}
    >
      <div className="container-custom">
        <div className="op-hero-inner">
          <div className="op-hero-kicker" data-testid="hero-kicker">
            <span className="op-kicker-dot" aria-hidden="true" />
            Premium Editorial Media
          </div>

          <h1 id="op-hero-title" data-testid="hero-title">
            The Daily Intelligence Layer for Real Opportunity in America
          </h1>

          <p className="op-hero-subtitle" data-testid="hero-subtitle">
            Timely insights, analysis, and opportunities across the sectors shaping careers, companies, and institutions.
          </p>

          <div className="op-hero-actions" data-testid="hero-actions">
            <Link
              to="/first-round"
              className="op-btn op-btn-primary"
              data-testid="hero-cta-signal"
            >
              Get Your Morning Edge
            </Link>
            <Link
              to="/career-hub"
              className="op-btn op-btn-dark"
              data-testid="hero-cta-opportunities"
            >
              Browse Career Hub
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

// ─── Fresh This Week Card ─────────────────────────────────────────────────────
const FreshThisWeekCard = ({ freshCount, jobsCount }) => (
  <div className="op-fresh-card" data-testid="fresh-this-week-strip">
    {/* Left: teal icon + title + description */}
    <div className="op-fresh-card-label">
      <div className="op-fresh-card-icon">
        <Grid2X2 size={18} strokeWidth={2} />
      </div>
      <div className="op-fresh-card-text">
        <span className="op-fresh-heading">Fresh This Week</span>
        <span className="op-fresh-sub">
          New opportunities every Monday.<br />Current stories archive after 10 days.
        </span>
      </div>
    </div>

    {/* Four stat cells with dividers */}
    <div className="op-fresh-cells">
      <div className="op-fresh-cell" data-testid="fresh-stat-stories">
        <strong>{freshCount > 0 ? freshCount : '23'}</strong>
        <span>Fresh Stories<br />This Week</span>
      </div>
      <div className="op-fresh-cell" data-testid="fresh-stat-jobs">
        <strong>{jobsCount > 0 ? jobsCount : '89'}</strong>
        <span>Active Opportunities<br />This Week</span>
      </div>
      <div className="op-fresh-cell op-fresh-cell--icon" data-testid="fresh-stat-audio">
        <Headphones size={24} strokeWidth={1.6} className="op-fresh-cell-icon" />
        <span>All Articles<br />Have Audio</span>
      </div>
      <div className="op-fresh-cell op-fresh-cell--icon" data-testid="fresh-stat-archive">
        <Clock size={24} strokeWidth={1.6} className="op-fresh-cell-icon op-fresh-cell-icon--navy" />
        <span>Stories Archive<br />After 10 Days</span>
      </div>
    </div>

    {/* How it works */}
    <Link to="/about" className="op-fresh-how-link" data-testid="fresh-how-it-works">
      How it works →
    </Link>
  </div>
);

// ─── Magazine Module ──────────────────────────────────────────────────────────
const MagazineModule = ({ magazine }) => {
  const coverStyle = magazine?.cover_image_url
    ? { '--op-cover-url': `url(${magazine.cover_image_url})` }
    : {};

  return (
    <div data-testid="magazine-module">
      <div className="op-section-head">
        <h2 className="op-section-title">Opportunity Post Monthly</h2>
        <Link to="/magazine" className="op-section-link" data-testid="magazine-archive-link">
          Archive →
        </Link>
      </div>

      <article className="op-magazine-card" data-testid="magazine-card">
        <div className="op-cover-art" style={coverStyle} data-testid="magazine-cover-art">
          <div className="op-cover-date" data-testid="magazine-issue-date">
            {magazine?.issue_date || 'Current Issue'}
          </div>
          <div className="op-cover-title" data-testid="magazine-cover-title">
            {magazine?.title || 'Current Issue'}
          </div>
          <div className="op-cover-badge">
            <strong>Special Report</strong>
            <span>↗</span>
          </div>
        </div>

        <div className="op-magazine-ctas">
          <Link
            to={magazine ? `/magazine/${magazine.slug}` : '/magazine'}
            className="op-btn op-btn-primary"
            data-testid="magazine-read-btn"
          >
            Read Online
          </Link>
          {magazine?.download_pdf_url ? (
            <a
              href={magazine.download_pdf_url}
              target="_blank"
              rel="noopener noreferrer"
              className="op-btn op-btn-light"
              data-testid="magazine-pdf-btn"
            >
              Download PDF
            </a>
          ) : (
            <Link to="/magazine" className="op-btn op-btn-light" data-testid="magazine-all-issues-btn">
              Download PDF
            </Link>
          )}
        </div>
      </article>
    </div>
  );
};

// ─── Signal Module ────────────────────────────────────────────────────────────
const SignalModule = ({ article }) => (
  <div data-testid="signal-module">
    <div className="op-section-head">
      <h2 className="op-section-title">
        The Opportunity Signal — Today's 3-Minute Briefing
      </h2>
      <Link to="/opportunity-signal" className="op-section-link" data-testid="signal-archive-link">
        Archive →
      </Link>
    </div>

    <article className="op-signal-card" data-testid="signal-card">
      <div className="op-signal-top">
        <button
          className="op-play-btn"
          aria-label="Play today's Signal briefing"
          data-testid="signal-play-btn"
        />
        <div className="op-signal-copy">
          <div className="op-eyebrow">Green Light Five</div>
          <h3 data-testid="signal-headline">
            {article?.title || 'Five opportunities worth acting on this week.'}
          </h3>
          <p data-testid="signal-description">
            {article?.deck ||
              'The Opportunity Signal compresses real opportunities, career movement, institutional news, and workforce shifts into a sharp 3-minute listen.'}
          </p>
          <Link
            to="/opportunity-signal"
            className="op-read-link"
            data-testid="signal-read-link"
          >
            Read full briefing →
          </Link>
        </div>
      </div>

      <div className="op-audio-row">
        <div className="op-timeline" aria-hidden="true" />
        <div className="op-audio-icons" aria-hidden="true">
          ◀ &nbsp; ● &nbsp; ▶
        </div>
      </div>
    </article>
  </div>
);

// ─── Gateway Section ──────────────────────────────────────────────────────────
const GatewaySection = ({ latestMagazine, newsroomImageUrl }) => (
  <section className="op-gateway-section" data-testid="gateway-section">
    <div className="container-custom">
      <div className="op-gateway-grid">
        <NewsroomPanel imageUrl={newsroomImageUrl || NEWSROOM_IMAGE_URL} />
        <FirstRoundPanel />
        <MagazineGatewayPanel magazine={latestMagazine} />
      </div>
    </div>
  </section>
);

// ─── First Round Panel ────────────────────────────────────────────────────────
const FR_AUDIO_URL = 'https://customer-assets-0z36b82j.emergentagent.net/job_c5fad414-0b45-4dfc-a09d-4f26483de910/artifacts/ihdv5kbe_fr_round%20_sample.mp3';

const FirstRoundPanel = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [elapsed,   setElapsed]   = useState(0);
  const [duration,  setDuration]  = useState(0);
  const audioRef = useRef(null);

  useEffect(() => {
    const audio = new Audio(FR_AUDIO_URL);
    audioRef.current = audio;
    const onTime  = () => setElapsed(audio.currentTime);
    const onMeta  = () => { if (isFinite(audio.duration)) setDuration(audio.duration); };
    const onEnded = () => { setIsPlaying(false); setElapsed(0); audio.currentTime = 0; };
    audio.addEventListener('timeupdate',      onTime);
    audio.addEventListener('loadedmetadata',  onMeta);
    audio.addEventListener('durationchange',  onMeta);
    audio.addEventListener('ended',           onEnded);
    return () => {
      audio.pause();
      audio.removeEventListener('timeupdate',     onTime);
      audio.removeEventListener('loadedmetadata', onMeta);
      audio.removeEventListener('durationchange', onMeta);
      audio.removeEventListener('ended',          onEnded);
    };
  }, []);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) { audio.pause(); setIsPlaying(false); }
    else           { audio.play();  setIsPlaying(true);  }
  };

  const progress = duration > 0 ? elapsed / duration : 0;
  const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
  const durLabel = duration > 0 ? fmt(duration) : '–:––';

  return (
    <div className="op-gateway-panel op-gw-audio" data-testid="gateway-first-round">
      {/* Header: product name + bell directly adjacent */}
      <div className="op-fr-header">
        <span className="op-fr-product">FIRST ROUND</span>
        <BellRing size={26} strokeWidth={1.4} className="op-fr-bell" />
      </div>

      {/* Main headline with gold period */}
      <h3 className="op-fr-headline">
        Your morning briefing on what matters next<span className="op-fr-dot">.</span>
      </h3>

      {/* Subline */}
      <p className="op-fr-sub">The 3-minute daily briefing from Opportunity Post.</p>

      {/* Gold divider rule */}
      <div className="op-fr-rule" />

      {/* Feature row: 4 items with dividers */}
      <div className="op-fr-features">
        <div className="op-fr-feature">
          <Timer size={17} strokeWidth={1.5} className="op-fr-feat-icon" />
          <span>3 MINUTES<br />TOTAL</span>
        </div>
        <div className="op-fr-feat-div" />
        <div className="op-fr-feature">
          <Newspaper size={17} strokeWidth={1.5} className="op-fr-feat-icon" />
          <span>TOP STORIES<br />CURATED</span>
        </div>
        <div className="op-fr-feat-div" />
        <div className="op-fr-feature">
          <Crosshair size={17} strokeWidth={1.5} className="op-fr-feat-icon" />
          <span>WHAT MATTERS<br />NEXT</span>
        </div>
        <div className="op-fr-feat-div" />
        <div className="op-fr-feature">
          <Bell size={17} strokeWidth={1.5} className="op-fr-feat-icon" />
          <span>DELIVERED<br />DAILY</span>
        </div>
      </div>

      {/* Player panel */}
      <div className="op-fr-player">
        <div className="op-fr-player-top">
          <div>
            <div className="op-fr-briefing-label">FIRST ROUND SAMPLE</div>
            <div className="op-fr-briefing-date">Sample Episode</div>
          </div>
          <div className="op-fr-dur">{durLabel}</div>
        </div>
        <div className="op-fr-play-row">
          <button
            className="op-fr-play-btn"
            onClick={togglePlay}
            aria-label={isPlaying ? 'Pause' : 'Play First Round'}
            data-testid="gateway-first-round-play"
          >
            {isPlaying ? (
              <svg width="17" height="17" viewBox="0 0 17 17" fill="white">
                <rect x="3" y="2.5" width="3.5" height="12" rx="1" />
                <rect x="10" y="2.5" width="3.5" height="12" rx="1" />
              </svg>
            ) : (
              <svg width="17" height="17" viewBox="0 0 17 17" fill="white">
                <polygon points="5.5,3 15,8.5 5.5,14" />
              </svg>
            )}
          </button>
          <div className="op-fr-wave-wrap" aria-hidden="true">
            <div className="op-fr-needle" style={{ left: `calc(${progress * 100}% - 1px)` }} />
            <svg className="op-fr-waveform" viewBox={`0 0 ${FR_WAVE_BARS.length * 8} 48`} preserveAspectRatio="none">
              {FR_WAVE_BARS.map((h, i) => {
                const played = (i / FR_WAVE_BARS.length) < progress;
                return (
                  <rect key={i} x={i * 8 + 1.5} y={(48 - h) / 2} width={5} height={h} rx={2.5}
                    fill={played ? '#D9A245' : '#2F9486'} fillOpacity={played ? 0.9 : 0.6} />
                );
              })}
            </svg>
          </div>
        </div>
        <div className="op-fr-time-row">
          <span>{fmt(elapsed)}</span>
          <span>{durLabel}</span>
        </div>
        <div className="op-fr-sample-link" data-testid="gateway-first-round-sample"
          onClick={togglePlay} style={{ cursor: 'pointer' }}>
          <Headphones size={13} strokeWidth={1.8} />
          <span>Listen to a sample</span>
          <ChevronRight size={13} strokeWidth={2} />
        </div>
      </div>

      {/* Green status strip — below player */}
      <div className="op-fr-status-strip">
        <span className="op-fr-status-rule" />
        <span className="op-fr-status-dot" />
        <span>Updated every weekday morning &middot; 5:30 AM PT / 8:30 AM ET</span>
        <span className="op-fr-status-rule" />
      </div>

      {/* Divider */}
      <div className="op-fr-sub-div" />

      {/* Subscribe row */}
      <div className="op-fr-subscribe-row">
        <div className="op-fr-mail-wrap">
          <Mail size={16} strokeWidth={1.7} className="op-fr-mail-ic" />
        </div>
        <div className="op-fr-subscribe-copy">
          <div className="op-fr-sub-title">Never miss a briefing</div>
          <div className="op-fr-sub-body">Delivered to your inbox every morning.</div>
        </div>
        <Link to="/newsletter" className="op-fr-sub-btn" data-testid="gateway-first-round-subscribe">
          Subscribe Free
        </Link>
      </div>
    </div>
  );
};

// ─── Newsroom Panel ───────────────────────────────────────────────────────────
const NewsroomPanel = ({ imageUrl }) => (
  <div className="op-gateway-panel op-gw-newsroom" data-testid="gateway-newsroom">
    <div className="op-gw-newsroom-header">
      <div className="op-gw-eyebrow">Newsroom</div>
      <h3 className="op-gw-newsroom-headline">
        The Intelligence Layer for Sector News & Opportunities
      </h3>
      <p className="op-gw-newsroom-sub">Curated News for Real-World Momentum</p>
    </div>
    <div className="op-gw-newsroom-img-wrap">
      <img src={imageUrl} alt="Opportunity Post Newsroom" className="op-gw-newsroom-img" />
    </div>
    <div className="op-gw-newsroom-footer">
      <p className="op-gw-newsroom-body">
        Briefings are updated daily to keep the most current information at your fingertips. Opportunity moves quickly in today's fast-paced world, and so do we, delivering articles across the sectors actively shaping careers, companies, and institutions. Stories remain live and accessible for 7 days, giving readers time to stay informed and make timely decisions. After that, they move to the member archive for future reference.
      </p>
      <Link to="/newsroom" className="op-gw-newsroom-cta" data-testid="gateway-newsroom-cta">
        Explore our Newsroom →
      </Link>
    </div>
  </div>
);

// ─── Magazine Gateway Panel ───────────────────────────────────────────────────
const MagazineGatewayPanel = ({ magazine }) => {
  const topMeta = [
    magazine?.issue_date,
    magazine?.volume_number && `Vol. ${magazine.volume_number}`,
    magazine?.issue_number && `No. ${magazine.issue_number}`,
  ].filter(Boolean).join(' · ');

  return (
    <div className="op-gateway-panel op-gw-magazine" data-testid="gateway-magazine">
      <div className="op-gw-eyebrow">Opportunity Post Magazine</div>
      <div className="op-gw-mag-label">Our Latest Monthly Issue</div>
      {topMeta && <div className="op-gw-mag-topmeta">{topMeta}</div>}

      <div className="op-gw-mag-cover-wrap">
        {magazine?.cover_image_url
          ? <img src={magazine.cover_image_url} alt={magazine.title || 'Current Issue'} className="op-gw-mag-cover" />
          : <div className="op-gw-mag-cover-placeholder"><span>OP</span></div>
        }
      </div>

      <Link
        to={magazine ? `/magazine/${magazine.slug}` : '/magazine'}
        className="op-gw-mag-cta-link op-gw-mag-bottom-cta"
        data-testid="gateway-magazine-read"
      >
        Read Web Edition →
      </Link>
    </div>
  );
};

// ─── Featured Opportunities Module ───────────────────────────────────────────
const WORK_MODE_LABELS = {
  remote: 'Remote', hybrid: 'Hybrid',
  on_site: 'On-Site', on_site_required: 'On-Site',
  full_time: 'Full-Time', part_time: 'Part-Time',
};

// Governed default Career Hub card image (Unsplash — diverse professionals)
const CAREER_HUB_DEFAULT_IMAGE =
  'https://images.unsplash.com/photo-1758691736975-9f7f643d178e?crop=entropy&cs=srgb&fm=jpg&q=80&w=800';

const FeaturedJobCard = ({ job }) => {
  const workLabel = WORK_MODE_LABELS[job.work_type] || job.work_type || '';
  const showCustom = job.use_custom_image && job.featured_image_url;
  const imgSrc = showCustom ? job.featured_image_url : CAREER_HUB_DEFAULT_IMAGE;

  return (
    <Link to={`/career-hub/${job.id}`} className="op-feat-job-card" data-testid="featured-job-card">
      {/* Image area */}
      <div className="op-feat-job-img">
        <img src={imgSrc} alt={job.title} className="op-feat-job-img-actual" />
        {job.category && (
          <span className="op-feat-job-cat-pill">
            {job.category.charAt(0).toUpperCase() + job.category.slice(1)}
          </span>
        )}
      </div>

      {/* Body */}
      <div className="op-feat-job-body">
        <div className="op-feat-job-chips">
          {job.location && <span className="op-feat-job-chip">{job.location}</span>}
          {workLabel && <span className="op-feat-job-chip op-feat-job-chip--mode">{workLabel}</span>}
        </div>
        <h4 className="op-feat-job-title" data-testid="featured-job-title">{job.title}</h4>
        <p className="op-feat-job-org" data-testid="featured-job-org">{job.organization}</p>
        {job.experience_level && (
          <p className="op-feat-job-type">{job.experience_level.replace(/_/g, ' ')}</p>
        )}
        <span className="op-feat-job-cta">View Role →</span>
      </div>
    </Link>
  );
};

const FeaturedOpportunitiesModule = ({ jobs }) => (
  <section className="op-feat-opps-section" data-testid="featured-opportunities-section">
    <div className="op-section-head">
      <div>
        <h2 className="op-section-title">Featured Opportunities</h2>
      </div>
      <div className="op-feat-opps-actions">
        <Link to="/career-hub" className="op-section-link" data-testid="view-all-opps-link">
          View All Opportunities →
        </Link>
        <span className="op-feat-opps-sep">·</span>
        <Link to="/career-hub/post" className="op-feat-opps-post-link" data-testid="post-job-link">
          Post a Job
        </Link>
      </div>
    </div>

    <div className="op-feat-opps-grid" data-testid="featured-opps-grid">
      {jobs.map(job => <FeaturedJobCard key={job.id} job={job} />)}
    </div>
  </section>
);

// ─── Opportunity Unlocked Widget ─────────────────────────────────────────────
const OpportunityUnlockedWidget = () => (
  <aside className="op-ou-widget" data-testid="op-unlocked-widget">
    {/* Top: icon + headline + body */}
    <div className="op-ou-top">
      <div className="op-ou-icon-wrap">
        <MailOpen size={24} strokeWidth={1.8} className="op-ou-icon" aria-hidden="true" />
      </div>
      <span className="op-ou-eyebrow" data-testid="op-unlocked-eyebrow">OPPORTUNITY UNLOCKED</span>
      <div className="op-ou-divider" aria-hidden="true">
        <span className="op-ou-divider-line" />
        <span className="op-ou-divider-star">✦</span>
        <span className="op-ou-divider-line" />
      </div>
      <h3 className="op-ou-headline" data-testid="op-unlocked-headline">Your Weekly Opportunity Brief</h3>
      <div className="op-ou-divider" aria-hidden="true">
        <span className="op-ou-divider-line" />
        <span className="op-ou-divider-star">✦</span>
        <span className="op-ou-divider-line" />
      </div>
      <p className="op-ou-body">
        The newsletter with timely insights and essential articles shaping what comes next.
      </p>
    </div>

    {/* Bottom: CTA button + fine print */}
    <div className="op-ou-bottom">
      <Link
        to="/subscribe?product=opportunity_unlocked"
        className="op-ou-btn"
        data-testid="op-unlocked-subscribe-btn"
      >
        Subscribe Free
      </Link>
      <span className="op-ou-fine">Delivered weekly. No spam, ever.</span>
    </div>
  </aside>
);

// ─── Trending Sectors Section ─────────────────────────────────────────────────
const TrendingSectorsSection = ({ contentItems }) => (
  <section className="op-sectors-section" data-testid="sectors-section">
    <div className="op-sectors-widget-layout">
      {/* Left column: heading + 3×2 sector cards */}
      <div className="op-ou-left-col">
        <div className="op-section-head" style={{ marginBottom: '16px' }}>
          <h2 className="op-section-title">Trending Sectors</h2>
        </div>
        <div className="op-pillars-grid op-pillars-grid--3col" data-testid="sectors-grid">
          {SECTORS_DEF.map((sector) => {
            const count = contentItems.filter((i) => i.primary_pillar === sector.key).length;
            return <SectorCard key={sector.key} sector={sector} count={count} />;
          })}
        </div>
      </div>
      {/* Right column: heading + Opportunity Unlocked widget */}
      <div className="op-ou-right-col">
        <div className="op-section-head" style={{ marginBottom: '16px' }}>
          <h2 className="op-section-title">Get Our Newsletter</h2>
        </div>
        <OpportunityUnlockedWidget />
      </div>
    </div>
  </section>
);

const SectorCard = ({ sector, count }) => {
  const { Icon } = sector;
  return (
    <div className="op-pillar-card op-sector-card" data-testid={`sector-card-${sector.key}`}>
      {/* Horizontal header: icon beside title */}
      <div className="op-sector-card-header">
        <div
          className="op-sector-icon-wrap"
          style={{ background: sector.iconBg, color: sector.accentRaw }}
        >
          <Icon size={22} strokeWidth={1.8} />
        </div>
        <div className="op-sector-name" style={{ color: sector.accentRaw }}>
          {sector.name}
        </div>
      </div>
      <p className="op-pillar-desc">{sector.desc}</p>
      <span className="op-pillar-count">
        {count} fresh post{count !== 1 ? 's' : ''} this week
      </span>
      <Link to={sector.link} className="op-pillar-cta" data-testid={`sector-cta-${sector.key}`}>
        {sector.cta} →
      </Link>
    </div>
  );
};

// ─── Forward / Newsletter ─────────────────────────────────────────────────────
